import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { openInvoice } from '@telegram-apps/sdk-react';
import type { InvoiceStatus } from '@telegram-apps/bridge';

import { SystemModalContainer } from '@/components/common/SystemModal/SystemModal.tsx';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { CandidateUI } from '@/api/matchmaker/types.ts';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import FireReactionShape from '@/components/common/svg/shapes/FireReactionShape.tsx';
import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { OFFERING_IDS } from '@/constants/offering_ids.ts';
import PurchaseController from '@/api/purchases/purchases.ts';
import { ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { helpers } from '@/helpers.ts';

interface ReactionsUpsaleCardProps {
  onClose: () => void;
  candidate: CandidateUI | null;
  onOpenPremiumPaywall: () => void;
  onOpenSendReaction: () => void;
}

const CandidateAvatar = ({ candidate }: { candidate: CandidateUI | null }) => {
  if (!candidate) return null;

  return (
    <div
      style={{
        position: 'relative',
        top: -40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '20px',
        marginBottom: '20px'
      }}
    >
      <ImageWithLoader
        fullWidth={false}
        style={{
          width: '136px',
          height: '136px',
          borderRadius: '50%',
          objectFit: 'cover',
          zIndex: 2
        }}
        src={
          helpers.getPrimaryPhoto(
            candidate.profile.v2photos?.[0]?.variants ?? []
          ) ?? ''
        }
        thumbhash={helpers.getThumbHash(
          candidate.profile.v2photos?.[0]?.variants ?? []
        )}
        alt="Contact"
      />
      <div
        style={{
          position: 'absolute',
          width: 39,
          height: 39,
          background: '#FFB634',
          bottom: -15,
          left: '50%',
          transform: 'translateX(-50%)',
          borderRadius: '50%',
          border: '2px solid #ffffff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3
        }}
      >
        <FireReactionShape width={17.5} height={17.5} fillColor="#ffffff" />
      </div>
    </div>
  );
};

export const ReactionsUpsalePopup = ({
  onClose,
  candidate,
  onOpenPremiumPaywall,
  onOpenSendReaction
}: ReactionsUpsaleCardProps) => {
  const { t } = useTranslation();
  const { aboutMe } = useProfileStore();
  const [isProcessing, setIsProcessing] = useState(false);

  const giftOffering = aboutMe?.billing_offerings?.find(
    (el) => el.id === OFFERING_IDS.giftId
  );

  const singleReactionPackage = giftOffering?.package_deals?.find(
    (p) => p.quantity === 3
  );

  const handleBuyOneReaction = async () => {
    if (!singleReactionPackage || isProcessing) return;

    setIsProcessing(true);

    try {
      const res = await PurchaseController.createInvoice({
        package_deal_id: singleReactionPackage.id,
        product_placement_name:
          ProductInvoicePlacements.MATCHMAKER_REACTIONS_AND_PREMIUM_UPSALE_POPUP
      });

      const status: InvoiceStatus = await openInvoice(res.invoice_url, 'url');

      if (status === 'paid') {
        onClose();
        onOpenSendReaction();
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGetPremium = () => {
    onClose();
    onOpenPremiumPaywall();
  };

  return (
    <SystemModalContainer headerTitle="" handleClose={onClose}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <CandidateAvatar candidate={candidate} />
      </div>

      <div
        style={{
          position: 'relative',
          top: -40
        }}
      >
        <div style={{ fontSize: '22px', fontWeight: 600, textAlign: 'center' }}>
          {t('upsale_reactions_card.title')}
        </div>

        <div
          style={{
            paddingBottom: '120px',
            textAlign: 'center',
            fontWeight: 500,
            fontStyle: 'medium',
            fontSize: '16px',
            lineHeight: '21px',
            color: '#5F5F5F',
            marginTop: '10px'
          }}
        >
          <Trans
            i18nKey="upsale_reactions_card.subtitle"
            components={{
              br: <br />
            }}
          />
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '5px 18px 15px 18px'
        }}
      >
        <MainButton
          onClick={handleBuyOneReaction}
          disabled={!singleReactionPackage || isProcessing}
          loading={isProcessing}
          text={
            <span>
              {t('upsale_reactions_card.one_time_purchase')}
              <TelegramCustomStar
                style={{
                  marginLeft: '8px',
                  marginRight: '8px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: '-2px'
                }}
                height={17}
                width={17}
                fillColor="#000000"
              />
              {singleReactionPackage?.amount ?? 100}
            </span>
          }
          containerStyle={{
            width: '90%',
            margin: '0 auto'
          }}
          buttonStyle={{
            borderRadius: '30px',
            background: 'linear-gradient(90deg, #FDCB40 2.95%, #FFB433 100%)',
            color: '#000000'
          }}
        />

        <MainButton
          onClick={handleGetPremium}
          disabled={isProcessing}
          loading={false}
          text={t('upsale_reactions_card.get_subscription')}
          containerStyle={{
            width: '90%',
            margin: '0 auto'
          }}
          buttonStyle={{
            border: '2px solid #000000',
            borderRadius: '30px',
            background: '#ffffff',
            color: '#000000'
          }}
        />
      </div>
    </SystemModalContainer>
  );
};
